import { useCallback, useMemo } from 'react'
import { getEnvName } from '@/utils/getEnvVars'
import type { LanguageCode } from '@sevenrooms/locales'
import { PaymentProvider } from '@sevenrooms/payments'
import { WidgetContainer } from '../../components'
import {
  useAppLoading,
  useVenue,
  useWidgetLanguage,
  useQueryParamAvailability,
  useGetSevenroomsLogin,
  useLanguageStrings,
  useReservationsRoute,
  useClientLogin,
} from '../../hooks'
import { updateFormState, useAppDispatch, useReservationFormState } from '../../store'
import { Checkout } from './Checkout'
import { CheckoutLoader } from './CheckoutLoader'
import { useCheckoutTotals } from './useCheckoutTotals'

const isProd = getEnvName() === 'PROD'

interface CheckoutPageProps {
  isModifyRoute?: boolean
}
export function CheckoutPage({ isModifyRoute }: CheckoutPageProps) {
  const dispatch = useAppDispatch()
  const { userInfo } = useClientLogin()
  const {
    id: venueId,
    largeLogoUrl,
    name,
    paymentType,
    paymentAccountId,
    paymentStripePublicKey,
    paymentIsFreedompayHpcEnabled,
    paymentIsFreedompayHpcFrictionlessHandlingEnabled,
    paymentIsFreedompayBypass3DsEnabled,
    paymentIsFreedompayAppleAndGooglePayEnabled,
    paymentIsFreedompayRequestOnlyPostalCode,
    paymentNiHostedSessionKey,
    currencyCode,
    countryCode,
  } = useVenue()
  const { availableLanguageOptions, selectedLanguage, setSelectedLanguage } = useWidgetLanguage()
  const { loadedLanguage } = useLanguageStrings()
  const isLoading = useAppLoading()
  const { hasQueryParamAvailability } = useQueryParamAvailability()
  const { formState } = useReservationFormState()
  const { linkToDefaultCreateReservationWidget } = useReservationsRoute()
  const isResDetailsLoading = isModifyRoute && !formState.actual

  const handleSetSelectedLanguage = useCallback(
    (language: LanguageCode) => {
      setSelectedLanguage(language)
      dispatch(updateFormState({ preferredLanguage: language }))
    },
    [dispatch, setSelectedLanguage]
  )

  const languagePicker = useMemo(() => {
    if (availableLanguageOptions.length <= 1) {
      return undefined
    }
    return {
      value: selectedLanguage,
      onChange: handleSetSelectedLanguage,
      options: availableLanguageOptions,
      disabled: isLoading,
    }
  }, [availableLanguageOptions, handleSetSelectedLanguage, isLoading, selectedLanguage])

  const sevenRoomsLogin = useGetSevenroomsLogin()
  const { dueNowAmount } = useCheckoutTotals()

  return (
    <WidgetContainer
      largeLogoUrl={largeLogoUrl}
      venueName={name}
      venueLink={linkToDefaultCreateReservationWidget}
      breadcrumbsPortalId="checkout-breadcrumbs"
      languagePicker={languagePicker}
      sevenRoomsLogin={sevenRoomsLogin}
    >
      {hasQueryParamAvailability || isResDetailsLoading ? (
        <CheckoutLoader />
      ) : (
        <PaymentProvider
          value={{
            venueId,
            venueName: name,
            paymentType,
            amount: dueNowAmount,
            currencyCode,
            countryCode: countryCode as string,
            paymentSettings: {
              useWallet: !!userInfo?.clientId,
              paymentAccountId,
              paymentStripePublicKey,
              selectedLanguage: loadedLanguage,
              paymentIsFreedompayHpcEnabled,
              paymentIsFreedompayHpcFrictionlessHandlingEnabled,
              paymentIsFreedompayBypass3DsEnabled,
              paymentIsFreedompayAppleAndGooglePayEnabled,
              paymentIsFreedompayRequestOnlyPostalCode,
              paymentNiHostedSessionKey,
              isProd,
            },
          }}
        >
          <Checkout isModifyRoute={isModifyRoute} />
        </PaymentProvider>
      )}
    </WidgetContainer>
  )
}
